/**
 * 1) « Mes cours à venir » (SPEC MC1, retour M3) — maquette 17-compte, bloc « À venir ».
 * Données et règles = UserProfile.tsx Sessions() + Visits/VisitsList.tsx + Visits/VisitItem.tsx :
 * filtre par marque, 4 prochains cours, spot révélé à la demande, annulation (modale v2, même requête).
 *
 * Filtre par marque : marques des cours à venir (plus de GET client/crossRegionalAssociations, retour
 * de Ségolène du 07/10 sur la vitesse : un appel Mindbody de moins ; et plus de marque proposée sans
 * aucun cours à venir).
 *
 * Visites en échec, rien en cache (erreur, ou pause hors ligne ; relecture du 09/10) : « Cours à venir indisponibles
 * pour le moment », à la place exacte du squelette (Ui.tsx UnavailableBlock), plus « Rien de réservé pour l'instant ».
 */
import { useListVisits } from 'hooks/useApiQueries';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { openCrossSite } from '../../../lib/crossSite';
import { hashUrl } from '../../../lib/planning/status';
import { IconExternal } from '../../Planning/icons';
import { sourceFailed } from '../lib/query';
import { UPCOMING_ON_ACCOUNT, upcomingSiteIds, upcomingVisits } from '../lib/visits';
import { CancelVisitButton, SpotReveal } from './VisitActions';
import { useVisitLookup } from '../hooks';
import { Dots, VisitRow } from './VisitRow';
import { BrandTag, SectionHead, Tag, UnavailableBlock } from './Ui';

export function Upcoming() {
  const visitsQuery = useListVisits() || {};
  const { data: visits, isLoading } = visitsQuery;
  const unavailable = sourceFailed(visitsQuery);
  const { studioOf, coachOf, brandOf, currentSiteId } = useVisitLookup();
  const [siteFilter, setSiteFilter] = useState<number | null>(null);
  const [cancelledIds, setCancelledIds] = useState<number[]>([]);

  // Marques des cours à venir (toutes marques), marque du site d'abord.
  const filterSites = useMemo(() => upcomingSiteIds(visits, currentSiteId), [visits, currentSiteId]);

  const upcoming = useMemo(() => upcomingVisits(visits, siteFilter), [visits, siteFilter]);
  const shown = upcoming.slice(0, UPCOMING_ON_ACCOUNT);

  return (
    <section className="mmv2-acct-section" aria-labelledby="mmv2-acct-upcoming">
      <SectionHead
        id="mmv2-acct-upcoming"
        title="Mes cours à venir"
        link={visits && visits.length > 0 ? { to: '/account/visits', label: 'Voir tous mes cours' } : undefined}
      />
      <div className="mmv2-card mmv2-card--pad mmv2-acct-block">
        {filterSites.length > 1 ? (
          <div className="mmv2-acct-filter">
            <label className="mmv2-acct-sr" htmlFor="mmv2-acct-brandfilter">
              Filtrer par marque
            </label>
            <select
              id="mmv2-acct-brandfilter"
              className="mmv2-acct-select"
              value={siteFilter ?? ''}
              onChange={(e) => setSiteFilter(e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">Toutes les marques</option>
              {filterSites.map((siteId) => (
                <option key={siteId} value={siteId}>
                  {brandOf(siteId).name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {unavailable ? (
          <UnavailableBlock>Cours à venir indisponibles</UnavailableBlock>
        ) : isLoading ? (
          <div className="mmv2-acct-skeleton" aria-busy="true" aria-label="Chargement de vos cours" />
        ) : shown.length ? (
          <ul className="mmv2-acct-vlist">
            {shown.map((visit, _i, all) => {
              const isCurrentSite = String(visit.SiteId) === String(currentSiteId);
              const cancelled = visit.AppointmentStatus === 'Cancelled' || cancelledIds.includes(visit.Id);
              const brand = brandOf(visit.SiteId);
              // Cours d'une autre marque : sa fiche s'ouvre sur le site de la marque, dans un nouvel onglet (comme au
              // planning, lib/crossSite.ts : passage de session compris).
              const crossHref = !isCurrentSite && visit.ClassId && brand.url ? hashUrl(brand.url, `/class/${visit.ClassId}`) : null;
              return (
                <VisitRow
                  key={visit.Id}
                  visit={visit}
                  muted={cancelled}
                  subline={
                    <Dots
                      parts={[
                        studioOf(visit.SiteId, visit.LocationId),
                        coachOf(visit.SiteId, visit.StaffId),
                        !cancelled ? <SpotReveal visit={visit} /> : null,
                      ]}
                    />
                  }
                  aside={
                    <>
                      {/* Liste qui mêle plusieurs marques : chaque cours porte sa marque, celle du site comprise (retour de
                          Morgane, Punch Annecy, 22/10). Une seule marque : étiquette seulement hors site courant. */}
                      {!isCurrentSite || new Set(all.map((v) => String(v.SiteId))).size > 1 ? <BrandTag brand={brand} /> : null}
                      {cancelled ? (
                        <Tag tone="neutral">Annulée</Tag>
                      ) : (
                        <>
                          {isCurrentSite && visit.ClassId ? (
                            <Link
                              className="mmv2-btn mmv2-btn--ghost mmv2-btn--sm mmv2-acct-visit__see"
                              to={`/class/${visit.ClassId}`}
                              aria-label={`Voir le cours ${visit.Name}`}
                            >
                              Voir
                            </Link>
                          ) : crossHref ? (
                            <a
                              className="mmv2-btn mmv2-btn--ghost mmv2-btn--sm mmv2-acct-visit__see"
                              href={crossHref}
                              target="_blank"
                              rel="noopener"
                              aria-label={`Voir le cours ${visit.Name} chez ${brand.name}, nouvel onglet`}
                              onClick={(event) => {
                                event.preventDefault();
                                openCrossSite(crossHref, String(visit.SiteId));
                              }}
                            >
                              Voir
                              <IconExternal className="mmv2-acct-visit__ext" />
                            </a>
                          ) : null}
                          <CancelVisitButton
                            visit={visit}
                            variant="quiet"
                            studioName={studioOf(visit.SiteId, visit.LocationId)}
                            onCancelled={() => setCancelledIds((ids) => [...ids, visit.Id])}
                          />
                        </>
                      )}
                    </>
                  }
                />
              );
            })}
          </ul>
        ) : (
          <div className="mmv2-acct-empty">
            <p className="mmv2-muted">
              {siteFilter
                ? 'Aucun cours réservé dans les studios de cette marque.'
                : 'Rien de réservé pour l’instant.'}
            </p>
            {!siteFilter ? (
              <Link className="mmv2-btn mmv2-btn--cta mmv2-btn--sm" to="/">
                Réserver un cours
              </Link>
            ) : null}
          </div>
        )}
      </div>
    </section>
  );
}
